import { useState } from "react";
import { Collapse } from "@chakra-ui/react";
import { Flex, Box, Button, Heading, Text } from "@/ui";
import ArrowDownIcon from "assets/icons/arrow_down_slim.svg?react";
import Time from "@/lib/Time";
import { t } from "@/utils";
import { useAuth } from "@/store/auth";
import { useDevices } from "@/store/devices";
import { getCameraDevices, getInactiveCameraDevices, getViewerDevices } from "@/store/devices/selectors";
import { useMount } from "@/hooks/useLifeCycles";
import { getDevicesLatestActivity } from "viewer/modules/cloud/station";
import Icon from "@/components/Icon";
import NavigationBar from "@/components/NavigationBar";
import { ViewerSettingsStepProps } from "../../SettingsFlow";
import DeviceCard from "./DeviceCard";

const getDeviceStatus = (deviceActivity?: DeviceActivity) => {
  return deviceActivity
    ? deviceActivity.status === "ONLINE"
      ? t("online")
      : t("offlineFor", { 0: Time.timeSince(deviceActivity.timestamp) })
    : null;
};

export default function DeviceList({ previous }: ViewerSettingsStepProps) {
  const [deviceActivities, setDeviceActivites] = useState<{
    [key: string]: DeviceActivity;
  }>({});
  const [showInactiveCameras, setShowInactiveCameras] = useState(false);
  const viewerDevices = useDevices(getViewerDevices);
  const cameraDevices = useDevices(getCameraDevices);
  const inactiveCameraDevices = useDevices(getInactiveCameraDevices);
  const _thisDevice = useAuth((state) => state.device);

  const getActivity = async () => {
    const activities = await getDevicesLatestActivity();
    if (!activities) return;
    const obj = {} as { [key: string]: DeviceActivity };
    activities.forEach((device) => (obj[device.jid] = device));
    setDeviceActivites(obj);
  };
  useMount(getActivity);

  const thisDevice = {
    name: _thisDevice.name,
    deviceName: _thisDevice.deviceName,
    jid: _thisDevice.xmppCredentials?.xmppLogin,
    role: "viewer"
  } as StoreViewerDevice;
  const listedViewers = thisDevice.jid ? [thisDevice, ...viewerDevices] : viewerDevices;

  return (
    <Box>
      <NavigationBar title={t("devices")} onBack={previous} />
      <Flex flexDir="column" rowGap="1rem">
        <Heading as="h5" mt="0.5rem">
          {t("viewers.title")}
        </Heading>
        <Flex justifyContent="space-between" columnGap="1.5rem" rowGap="1rem" flexWrap="wrap">
          {listedViewers.map((device) => {
            const status = getDeviceStatus(deviceActivities[device.jid]);
            return (
              <DeviceCard
                key={device.jid}
                device={device}
                isViewerDevice
                isThisDevice={thisDevice.jid === device.jid}
                subtitle={status}
              />
            );
          })}
        </Flex>
        {cameraDevices.length > 0 && (
          <>
            <Heading as="h5" mt="0.5rem">
              {t("cameras.title")}
            </Heading>
            <Flex justifyContent="space-between" columnGap="1.5rem" rowGap="1rem" flexWrap="wrap">
              {cameraDevices.map((device) => {
                const status = getDeviceStatus(deviceActivities[device.jid]);
                return <DeviceCard key={device.jid} device={device} subtitle={status} />;
              })}
            </Flex>
          </>
        )}
        {inactiveCameraDevices.length > 0 && (
          <>
            <Button
              variant="unstyled"
              display="flex"
              alignItems="center"
              alignSelf="flex-start"
              columnGap="0.25rem"
              px="0.75rem"
              py="0.5rem"
              mt="0.5rem"
              mb="-0.5rem"
              ml="-0.75rem"
              bg="surface2"
              whiteSpace="normal"
              textAlign="start"
              onClick={() => setShowInactiveCameras((prev) => !prev)}
            >
              <Text variant="title">{t("cameras.showSwitched")}</Text>
              <Icon
                icon={ArrowDownIcon}
                size="1rem"
                fill="on-surface"
                style={{
                  transition: "transform 0.2s ease-in-out",
                  transform: showInactiveCameras ? "rotate(180deg)" : "rotate(0deg)"
                }}
              />
            </Button>
            <Collapse in={showInactiveCameras} unmountOnExit>
              <Flex justifyContent="space-between" columnGap="1.5rem" rowGap="1rem" flexWrap="wrap" mb="10">
                {inactiveCameraDevices.map((device) => {
                  return <DeviceCard key={device.jid} device={device} subtitle={device.deviceName} />;
                })}
              </Flex>
              <Text variant="body" mb="0.5rem">
                {t("cameras.switched.explanation")}
              </Text>
            </Collapse>
          </>
        )}
      </Flex>
    </Box>
  );
}
